import { Image as ImageComponent } from '@/Components/UI/Image';
import { twMerge } from 'tailwind-merge';
import { useApi, useFormInput } from '@/hooks';
import { ChangeEventHandler, useEffect, useState } from 'react';
import { Error, Group, Label } from '@/Components/UI/Form/Partials';
import { FormInputProps, Image } from '@/types';
import { Button } from '@/Components/UI/Button';
import { Icon } from '@/Components/UI/Icon';

type ImageInputProps = FormInputProps<unknown> & {
    onClear?: () => void,
};

export const ImageInput = ({
                               id,
                               name,
                               label,
                               required = false,
                               disabled = false,
                               groupClassName,
                               onClear,
                           }: ImageInputProps) => {
    const { value, setValue, error } = useFormInput<Image | undefined>(name);

    const { ImageService } = useApi();
    const [isLoading, setIsLoading] = useState(false);
    const [uploadError, setUploadError] = useState(undefined);

    const onChangeImage: ChangeEventHandler<HTMLInputElement> = (e) => {
        if (!e.target.files) {
            return;
        }

        setIsLoading(true);

        ImageService
            .uploadImage(e.target.files[0])
            .then((response) => setValue(response.data))
            .catch((error) => setUploadError(error))
            .finally(() => setIsLoading(false));
    };

    const clear = () => {
        setValue(undefined);

        if (onClear) {
            onClear();
        }
    }

    useEffect(() => {
        setUploadError(undefined);
    }, [value]);

    return (
        <Group className={groupClassName}>
            <Label
                htmlFor={id ?? name}
                required={required}
                disabled={disabled}
                invalid={error !== undefined}>
                {label}
            </Label>

            <div
                className={twMerge(
                    'form-input block w-full rounded-lg border border-surface-3 bg-surface-3 p-2.5 text-on-surface outline-hidden focus:outline-hidden transition-colors hover:bg-surface-5 focus:border-primary focus:bg-surface-5 focus:ring-primary focus-visible:ring-primary',
                    disabled && 'cursor-not-allowed bg-surface-1 hover:bg-surface-1',
                    error !== undefined && 'border-error focus:border-error focus-visible:ring-error'
                )}>
                <label className={'block relative'}>
                    <input
                        type={'file'}
                        accept={'image/*'}
                        disabled={disabled}
                        hidden={true}
                        onChange={onChangeImage} />

                    <ImageComponent
                        className={'size-full aspect-video rounded-md'}
                        image={value}
                        alt={label ?? ''} />

                    <div className={'absolute top-2 right-2 flex gap-2'}>
                        {uploadError && (
                            <Button
                                buttonStyle={'tonal'}
                                type={'button'}
                                disabled={true}
                                className={'p-0 size-10 aspect-square opacity-70 text-error'}>
                                <Icon name={'error'} />
                            </Button>
                        )}

                        <Button
                            buttonStyle={'tonal'}
                            type={'button'}
                            disabled={disabled || isLoading}
                            onClick={clear}
                            className={'p-0 size-10 aspect-square opacity-70'}>
                            <Icon name={'delete'} />
                        </Button>
                    </div>

                    {isLoading && (
                        <div
                            className={'absolute inset-0 flex items-center justify-center rounded-md bg-surface-3/50 text-primary'}>
                            <Icon name={'progress_activity'} className={'text-4xl animate-spin'} />
                        </div>
                    )}
                </label>
            </div>

            <Error error={error} />
        </Group>
    );
};
